import type { ViewJobReadableCookies } from "../../../shared/native-modules/core/getCookies";

import { enrichJobResultTrackingKey } from "./enrichJobResultTrackingKey";
import { useBrandedJobProctorData } from "./useBrandedJobProctorData";
import { deleteCookiesForViewJob } from "../../../shared/native-modules/core/deleteCookies";
import { useTareData } from "../../../shared/native-modules/tare/TareDataContext";
import { CookieKey } from "../../../shared/native-modules/types/RNCoreNativeModule";
import { useCookies } from "../../../shared/util/CookieProvider";
import { isSSR } from "../../../shared/util/isSSR";
import { useViewjobProctorAllocation } from "../../proctor/__generated__/ViewjobProctorContext";
import {
  MobVjApplyRateLimitTst,
  RnJsProMobVjEmpInsightsTst,
  RnJsProVjProInsightsTog,
} from "../../proctor/__generated__/ViewjobProctorSpecifications";
import { useViewjobQuery } from "../__generated__/viewjob.gqlgenerated";

import { useEffect, useMemo } from "react";
import { Platform } from "react-native";

export function useViewJobData(url: string, jobKey: string) {
  const cookies = useCookies<ViewJobReadableCookies>();
  const tareData = useTareData();
  const isLoggedIn = tareData?.accountStatus?.isLoggedIn ?? false;
  const mobVjApplyRateLimitAllocation = useViewjobProctorAllocation(MobVjApplyRateLimitTst);
  const { shouldQueryBrandingEligibility } = useBrandedJobProctorData();
  const isWebSSR = Platform.OS === "web" && isSSR();

  // Delete MCLK cookie after reading (aligns with getVJData native method)
  useEffect(() => {
    void deleteCookiesForViewJob([CookieKey.clickTrackingLog]);
  }, []);

  const cookieModel = useMemo(() => {
    return {
      clickTrackingLog: cookies[CookieKey.clickTrackingLog] ?? undefined,
      previousPageNumber: cookies[CookieKey.previousPageNumber] ?? undefined,
      publisherNumber: cookies[CookieKey.publisherNumber] ?? undefined,
      applyCountHistogram: cookies[CookieKey.applyCountHistogram] ?? undefined,
      jobseekerCookieModel: {
        referral: cookies[CookieKey.referral] ?? undefined,
      },
    };
  }, [cookies]);

  const jobResultTrackingKey = useMemo(() => enrichJobResultTrackingKey(url), [url]);

  const shouldQueryApplyRateLimit = mobVjApplyRateLimitAllocation.value === 1 && isLoggedIn;
  const isJsProVjProInsightsTogActive =
    useViewjobProctorAllocation(RnJsProVjProInsightsTog).value > 0;
  const isJsProMobVjEmpInsightsTstActive =
    useViewjobProctorAllocation(RnJsProMobVjEmpInsightsTst).value > 0;
  const enableEmployerInsights = isJsProMobVjEmpInsightsTstActive && isJsProVjProInsightsTogActive;

  const variables = useMemo(
    () => ({
      input: jobKey,
      detailsInput: {
        viewjobUrl: url,
        viewjobCookieModel: cookieModel,
        shouldQueryApplyRateLimit,
      },
      jobResultTrackingKey,
      isLoggedIn,
      enableEmployerInsights,
      shouldQueryBrandingEligibility,
      // Gates every field sourced from the RNW SSR ViewJob OneGraph experiment.
      includeWebSsrOnegraphFields: isWebSSR,
    }),
    [
      cookieModel,
      jobKey,
      url,
      jobResultTrackingKey,
      isLoggedIn,
      shouldQueryApplyRateLimit,
      enableEmployerInsights,
      shouldQueryBrandingEligibility,
      isWebSSR,
    ],
  );

  return useViewjobQuery({
    variables,
    // Allow Apollo to return partial data for non-critical GraphQL field errors.
    errorPolicy: "all",
  });
}
